/**
 * Recommendation domain color constants.
 * All colours here are intentionally domain-specific and not in global tokens —
 * they map directly to recommendation lifecycle states and causal node types.
 * Import from this file instead of hardcoding hex strings in components.
 */

// Shared topbar height used for drawer positioning
export const TOPBAR_H = 56;

// Status badge meta — shared between page list and drawer
export const REC_STATUS_META = {
  unacknowledged: { label: 'New',      short: 'New',     color: '#64748b', bg: 'rgba(100,116,139,0.10)', border: 'rgba(100,116,139,0.25)' },
  accepted:       { label: 'Acknowledged', short: 'Ack',     color: '#0891b2', bg: 'rgba(8,145,178,0.10)',   border: 'rgba(8,145,178,0.25)'   },
  acted_on:       { label: 'Acted On', short: 'Acted',   color: '#16a34a', bg: 'rgba(22,163,74,0.10)',   border: 'rgba(22,163,74,0.25)'   },
  rejected:       { label: 'Rejected', short: 'Rej',     color: '#dc2626', bg: 'rgba(220,38,38,0.10)',   border: 'rgba(220,38,38,0.25)'   },
  expired:        { label: 'Expired', short: 'Expired', color: '#94a3b8', bg: 'rgba(148,163,184,0.10)', border: 'rgba(148,163,184,0.25)' },
};

// Impact badge meta
export const REC_IMPACT_META = {
  high:   { label: 'High',   color: '#dc2626', bg: 'rgba(220,38,38,0.08)',   border: 'rgba(220,38,38,0.2)' },
  medium: { label: 'Medium', color: '#d97706', bg: 'rgba(217,119,6,0.08)',   border: 'rgba(217,119,6,0.2)' },
  low:    { label: 'Low',    color: '#2563eb', bg: 'rgba(37,99,235,0.08)',   border: 'rgba(37,99,235,0.2)' },
};

// Timeline / chat entry icon + colour meta
export const REC_TIMELINE_META = {
  raised:   { icon: 'lightbulb',    color: '#f59e0b' },
  accepted: { icon: 'check_circle', color: '#0891b2' },
  acted_on: { icon: 'task_alt',     color: '#16a34a' },
  rejected: { icon: 'cancel',       color: '#dc2626' },
  remark:   { icon: 'chat',         color: '#8b5cf6' },
  expired:  { icon: 'schedule',     color: '#94a3b8' },
};

// Causal chain node type colours
// Causal chain node colours — keys match paramTypeMeta in tokens.js
export const CAUSAL_NODE_TYPE = {
  target:       { bg: 'rgba(30,67,150,0.08)',   border: 'rgba(30,67,150,0.6)',   text: '#1e4396', label: 'Target'       },
  controllable: { bg: 'rgba(16,185,129,0.08)',  border: 'rgba(16,185,129,0.6)',  text: '#059669', label: 'Controllable' },
  observed:       { bg: 'rgba(245,158,11,0.08)',  border: 'rgba(245,158,11,0.6)',  text: '#d97706', label: 'Observed'      },
  monitored:      { bg: 'rgba(139,92,246,0.08)',  border: 'rgba(139,92,246,0.6)',  text: '#7c3aed', label: 'Monitored'      },
  unclassified:   { bg: 'rgba(100,116,139,0.08)', border: 'rgba(100,116,139,0.5)', text: '#64748b', label: 'Unclassified'   },
};

// Lifecycle timeline node colours (maps to REC_STATUS_META subset + raised)
export const LC_META = {
  raised:   { icon: 'lightbulb',    color: '#f59e0b', label: 'Raised'   },
  accepted: { icon: 'check_circle', color: '#0891b2', label: 'Accepted' },
  acted_on: { icon: 'task_alt',     color: '#16a34a', label: 'Acted On' },
  rejected: { icon: 'cancel',       color: '#dc2626', label: 'Rejected' },
  expired:  { icon: 'schedule',     color: '#94a3b8', label: 'Expired'  },
};

// Furnace state pill colours
export const PARAM_STATE_META = {
  in_range:     { label: 'In Range',     color: '#16a34a', bg: 'rgba(22,163,74,0.10)'    },
  out_of_range: { label: 'Out of Range', color: '#dc2626', bg: 'rgba(220,38,38,0.10)'   },
  off:          { label: 'Off',          color: '#94a3b8', bg: 'rgba(148,163,184,0.10)' },
};

// Action options for live state section
export const ACTION_OPTS = [
  { key: 'accepted', label: 'Acknowledge',   icon: 'check_circle', color: '#0891b2', bg: 'rgba(8,145,178,0.08)',  border: 'rgba(8,145,178,0.3)'  },
  { key: 'acted_on', label: 'Mark Acted On', icon: 'task_alt',     color: '#16a34a', bg: 'rgba(22,163,74,0.08)',  border: 'rgba(22,163,74,0.3)'  },
  { key: 'rejected', label: 'Reject',        icon: 'cancel',       color: '#dc2626', bg: 'rgba(220,38,38,0.08)',  border: 'rgba(220,38,38,0.3)'  },
];

// "Same as last N" badge colours (monitored-type purple from paramTypeMeta)
export const REPEAT_BADGE = {
  color:  '#7c3aed',
  bg:     'rgba(124,58,237,0.08)',
  border: 'rgba(124,58,237,0.2)',
};

// Recommendation accent colour (amber/yellow — the lightbulb brand colour for this feature)
export const REC_ACCENT = '#f59e0b';

// Action button / highlight colour (brand navy, matches semantic.brandNavyMid)
export const REC_BRAND = '#1e4396';
